import React, { useContext } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '../Contexts/ThemeContext';
import { Route, Switch, useRouteMatch, Redirect } from 'react-router-dom';
import { DataDisplayProvider, PendoInitializer, SidebarNavProvider } from '@aidkitorg/component-library';
import { PaymentsPage } from './Payments/PaymentsPage';
import { PaymentBatchDetailsPage } from './Payments/PaymentBatchDetailsPage';
import { PaymentsProvider } from '../Contexts/PaymentsContext';
import { AdminPanelPage } from './AdminPanelPage';
import { AccountsPage } from './Accounts/AccountsPage';
import { get_deployment } from '../API';
import { ConfigurationContext, PublicConfigurationContext, UserInfoContext } from '../Context';
import { AccountsProvider } from '../Contexts/AccountsContext';
import { DashboardsPage } from './Dashboards/DashboardsPage';
import { DashboardBuilderPage } from './Dashboards/DashboardBuilderPage';
import { useLocalizedStrings } from '../Localization';
import { DashboardsProvider } from '../Contexts/DashboardsContext';
import { DashboardPage } from './Dashboards/DashboardPage';
import { SettingsProvider } from '../Contexts/SettingsContext';
import { ManageFieldsPage } from './Settings/ManageFieldsPage';
import { ReportsPage } from './Reports/ReportsPage';
import { ReportsBuilderPage } from './Reports/ReportsBuilderPage';
import { ReportViewPage } from './Reports/ReportViewPage';
import { ReportsProvider } from '../Contexts/ReportsContext';
import { NotificationsPage } from './Notifications/NotificationsPage';
import { NotificationRecipientsPage } from './Notifications/NotificationRecipientsPage';
import { NotificationsProvider } from '../Contexts/NotificationsContext';
import { getNavItems, getParentRedirectRoutes } from './navigationConfig';
import { OrganizationProvider } from '../Contexts/OrganizationContext';
import { UserProvider } from '../Contexts/UserContext';
import { UserManagementPage } from './Settings/UserManagementPage';
import { UserGroupManagementPage } from './Settings/UserGroupManagementPage';
import { UserGroupDetailPage } from './Settings/UserGroupDetailPage';
import { OrganizationManagementPage } from './Settings/OrganizationManagementPage';
import { OnSiteIntakePage } from './Applicants/OnSiteIntakePage';
import { AuditLogsPage } from './AuditLogs/AuditLogsPage';
import { AuditLogsProvider } from '../Contexts/AuditLogsContext';
import { ProgramHomePage } from './ProgramHome/ProgramHomePage';
import { TransactionsPage } from './Transactions/TransactionsPage';
import { TransactionsProvider } from '../Contexts/TransactionsContext';
import { InvoicesPage } from './Invoices/InvoicesPage';
import { InvoicesProvider } from '../Contexts/InvoicesContext';
import { ProgramHomeProvider } from '../Contexts/ProgramHomeContext';
import { SupportPage } from './Support/SupportPage';
import { SupportCaseDetailPage } from './Support/SupportCaseDetailPage';
import { ApplicantsProvider } from '../Contexts/ApplicantsContext';
import { ReviewQueuesPage } from './Reviews/ReviewQueuesPage';
import { MyReviewsPage } from './Reviews/MyReviewsPage';
import { MyReviewsProvider } from '../Contexts/MyReviewsContext';
import { EditApplicantGroupPage } from './Applicants/ApplicantGroups/EditApplicantGroupPage';
import ApplicantGroupDetailPage from './Applicants/ApplicantGroups/ApplicantGroupDetailPage';
import { CreateApplicantGroupPage } from './Applicants/ApplicantGroups/CreateApplicantGroupPage';
import ApplicantGroupsPage from './Applicants/ApplicantGroups/ApplicantGroupsPage';
import AllApplicantsPage from './Applicants/AllApplicantsPage';
import { AdminApplicantRouteWrapper } from './Reviews/AdminApplicantRouteWrapper';
import ImportApplicantsPage from './Applicants/ImportApplicantsPage';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
      staleTime: 30000 // 30 seconds
    }
  }
});

export const AdminPanelApp = () => {
  let { path } = useRouteMatch();
  const publicConfig = useContext(PublicConfigurationContext);
  const userInfo = useContext(UserInfoContext);
  const config = useContext(ConfigurationContext);
  const programName = get_deployment();
  const L = useLocalizedStrings();

  // Get navigation items and generate automatic redirects
  const navItems = getNavItems(L, userInfo.tags, config.user?.email);
  const parentRedirects = getParentRedirectRoutes(navItems);

  return (
    <ThemeProvider>
      <DataDisplayProvider value={{ displayInUTC: false }}>
        <OrganizationProvider>
          <SidebarNavProvider>
            {
              publicConfig.experimental?.enablePendo && (
                <PendoInitializer visitorData={{
                  id: userInfo.uid || "unknown"
                }} accountData={{
                  id: programName || "unknown"
                }} />)
            }
            <Switch>
              <Route exact path={`${path}`}>
                <Redirect to={`${path}/home`} />
              </Route>

              {/* Automatic redirects for parent routes to their first child */}
              {parentRedirects.map((redirect, index) => (
                <Route
                  key={`redirect-${index}`}
                  exact
                  path={redirect.from}
                  render={() => <Redirect to={redirect.to} />}
                />
              ))}

              <Route
                path={`${path}/home`}
                children={
                  <ProgramHomeProvider>
                    <ProgramHomePage />
                  </ProgramHomeProvider>
                }
              />

              {/* REVIEW ROUTES */}
              <Route
                exact
                path={`${path}/reviews/my-reviews`}
                render={() => (
                  <MyReviewsProvider>
                    <MyReviewsPage />
                  </MyReviewsProvider>
                )}
              />
              <Route
                exact
                path={`${path}/reviews/queues`}
                component={ReviewQueuesPage}
              />

              <Route
                path={`${path}/a/:uid/:subsurvey?`}
                component={AdminApplicantRouteWrapper}
              />

              <Route
                path={`${path}/audit-logs`}
                children={
                  <QueryClientProvider client={queryClient}>
                    <AuditLogsProvider>
                      <AuditLogsPage />
                    </AuditLogsProvider>
                  </QueryClientProvider>
                }
              />
              <Route
                exact
                path={`${path}/support/:caseId`}
                children={
                  <QueryClientProvider client={queryClient}>
                    <SupportCaseDetailPage />
                  </QueryClientProvider>
                }
              />
              <Route
                exact
                path={`${path}/support`}
                children={
                  <QueryClientProvider client={queryClient}>
                    <SupportPage />
                  </QueryClientProvider>
                }
              />

              {/* APPLICANTS ROUTES */}
              <Route
                exact
                path={`${path}/applicants/all`}
                render={() => (
                  <QueryClientProvider client={queryClient}>
                    <ApplicantsProvider>
                      <AllApplicantsPage />
                    </ApplicantsProvider>
                  </QueryClientProvider>
                )}
              />
              <Route
                exact
                path={`${path}/applicants/all/import`}
                render={() => (
                  <QueryClientProvider client={queryClient}>
                    <ApplicantsProvider>
                      <ImportApplicantsPage />
                    </ApplicantsProvider>
                  </QueryClientProvider>
                )}
              />

              {/* APPLICANT GROUPS ROUTES */}
              <Route
                exact
                path={`${path}/applicants/applicant-groups`}
                render={() => (
                  <SettingsProvider>
                    <ApplicantGroupsPage />
                  </SettingsProvider>
                )}
              />
              <Route
                exact
                path={`${path}/applicants/applicant-groups/create`}
                render={() => (
                  <SettingsProvider>
                    <CreateApplicantGroupPage />
                  </SettingsProvider>
                )}
              />
              <Route
                exact
                path={`${path}/applicants/applicant-groups/:id/edit`}
                render={() => (
                  <SettingsProvider>
                    <EditApplicantGroupPage />
                  </SettingsProvider>
                )}
              />
              <Route
                exact
                path={`${path}/applicants/applicant-groups/:id`}
                render={() => (
                  <SettingsProvider>
                    <ApplicantGroupDetailPage />
                  </SettingsProvider>
                )}
              />

              <Route
                exact
                path={`${path}/applicants/onsite-intake`}
                render={() => (
                  <QueryClientProvider client={queryClient}>
                    <OnSiteIntakePage />
                  </QueryClientProvider>
                )}
              />

              {/* PAYMENTS ROUTES */}
              <Route
                exact
                path={`${path}/payments`}
                children={
                  <PaymentsProvider>
                    <PaymentsPage />
                  </PaymentsProvider>
                }
              />
              <Route
                path={`${path}/payments/payment-details/:batch_name`}
                children={
                  <PaymentsProvider>
                    <PaymentBatchDetailsPage />
                  </PaymentsProvider>
                }
              />
              <Route
                path={`${path}/payments/:uid`}
                component={AdminApplicantRouteWrapper}
              />

              {/* ACCOUNTS ROUTES */}
              <Route
                path={`${path}/balances`}
                children={
                  <AccountsProvider>
                    <AccountsPage />
                  </AccountsProvider>
                }
              />

              {/* TRANSACTIONS ROUTE */}
              <Route
                path={`${path}/transactions`}
                children={
                  <TransactionsProvider>
                    <AccountsProvider>
                      <TransactionsPage/>
                    </AccountsProvider>
                  </TransactionsProvider>
                }
              />

              {/* INVOICES ROUTE */}
              <Route
                path={`${path}/invoices`}
                children={
                  <InvoicesProvider>
                    <InvoicesPage />
                  </InvoicesProvider>
                }
              />

              {/* DASHBOARDS ROUTES */}
              <Route
                exact
                path={`${path}/dashboards`}
                children={
                  <ReportsProvider>
                    <DashboardsProvider>
                      <DashboardsPage />
                    </DashboardsProvider>
                  </ReportsProvider>
                }
              />
              <Route
                exact
                path={`${path}/dashboards/new`}
                children={
                  <ReportsProvider>
                    <DashboardsProvider>
                      <SettingsProvider>
                        <DashboardBuilderPage />
                      </SettingsProvider>
                    </DashboardsProvider>
                  </ReportsProvider>
                }
              />
              <Route
                path={`${path}/dashboards/:dashboardId/edit`}
                children={
                  <ReportsProvider>
                    <DashboardsProvider>
                      <SettingsProvider>
                        <DashboardBuilderPage />
                      </SettingsProvider>
                    </DashboardsProvider>
                  </ReportsProvider>
                }
              />
              <Route
                path={[`${path}/dashboards/:dashboard_id/:dashboard_title`, `${path}/dashboards/:dashboard_id`]}
                children={
                  <ReportsProvider>
                    <DashboardsProvider>
                      <SettingsProvider>
                        <DashboardPage />
                      </SettingsProvider>
                    </DashboardsProvider>
                  </ReportsProvider>
                }
              />

              {/* REPORTS ROUTES */}
              <Route
                exact
                path={`${path}/reports`}
                render={() => (
                  <ReportsProvider>
                    <ReportsPage />
                  </ReportsProvider>
                )}
              />
              <Route
                exact
                path={`${path}/reports/new`}
                render={() => (
                  <ReportsProvider>
                    <SettingsProvider>
                      <ReportsBuilderPage />
                    </SettingsProvider>
                  </ReportsProvider>
                )}
              />
              <Route
                exact
                path={`${path}/reports/:reportId/edit`}
                render={(routeProps) => (
                  <ReportsProvider key={routeProps.match.params.reportId}>
                    <SettingsProvider>
                      <ReportsBuilderPage />
                    </SettingsProvider>
                  </ReportsProvider>
                )}
              />
              <Route
                path={`${path}/reports/:reportId`}
                render={(routeProps) => (
                  <ReportsProvider key={routeProps.match.params.reportId}>
                    <ReportViewPage />
                  </ReportsProvider>
                )}
              />

              {/* USER MANAGEMENT ROUTES */}
              <Route
                exact
                path={`${path}/user-management/users`}
                render={() => (
                  <UserProvider>
                    <UserManagementPage />
                  </UserProvider>
                )}
              />
              <Route
                exact
                path={`${path}/user-management/organizations`}
                render={() => (
                  <OrganizationManagementPage />
                )}
              />
              <Route
                exact
                path={`${path}/user-management/user-groups`}
                render={() => (
                  <UserProvider>
                    <UserGroupManagementPage />
                  </UserProvider>
                )}
              />
              <Route
                path={`${path}/user-management/user-groups/:groupId`}
                render={() => (
                  <UserProvider>
                    <UserGroupDetailPage />
                  </UserProvider>
                )}
              />

              {/* SETTINGS ROUTES */}
              <Route
                exact
                path={`${path}/settings/manage-fields`}
                render={() => (
                  <SettingsProvider>
                    <ManageFieldsPage />
                  </SettingsProvider>
                )}
              />

              {/* NOTIFICATIONS ROUTES */}
              <Route
                exact
                path={`${path}/notifications`}
                children={
                  <QueryClientProvider client={queryClient}>
                    <NotificationsProvider>
                      <NotificationsPage />
                    </NotificationsProvider>
                  </QueryClientProvider>
                }
              />
              <Route
                path={`${path}/notifications/notification-details/:targetFieldPrefix`}
                children={
                  <QueryClientProvider client={queryClient}>
                    <NotificationsProvider>
                      <NotificationRecipientsPage />
                    </NotificationsProvider>
                  </QueryClientProvider>
                }
              />
              {/* Catch-all redirect to home */}
              <Route path="*">
                <Redirect to={`${path}/home`} />
              </Route>
            </Switch>
          </SidebarNavProvider>
        </OrganizationProvider>
      </DataDisplayProvider>
    </ThemeProvider>
  )
}

export default AdminPanelApp;
